<template>
  <v-card
    outlined
    class="insight-card d-flex flex-column justify-space-between h-100"
  >
    <FeedbackChip
      v-if="showFeedbackChip && !feedbackGiven"
      :widget="widgetName"
      @feedbackGiven="feedbackGiven = true"
    />
    <v-card-title
      class="ma-0 pa-0 d-flex flex-column justify-start align-start rounded-sm rounded-b-0 pb-2 pt-3"
    >
      <div class="d-flex justify-space-between w-100 align-center px-4 mb-2">
        <p
          class="ma-0 pa-0 blue-grey--text text--darken-3 subtitle-2 font-weight-600"
        >
          {{ name }}
        </p>
        <v-tooltip bottom max-width="380" min-width="300px">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on" color="blue-grey" size="16"
              >mdi-information-outline</v-icon
            >
          </template>
          {{ info }}
        </v-tooltip>
      </div>
      <div class="d-flex align-center justify-space-between w-100">
        <template v-if="canSelectSite && !showWidgetError">
          <WidgetMenu
            closeOnContentClick
            data-testid="widgetBase-menu-locationSelector"
            :options="locationOptions"
            :selectedOption="selectedLocationOptionId"
            @click="setLocationOption"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                data-testid="widgetBase-button-locationSelector"
                outlined
                small
                color="blue-grey lighten-3"
                class="mx-2 remove-outline-button px-2"
                :disabled="showLoading || loadingCompare"
                :ripple="false"
              >
                <p
                  class="ma-0 pa-0 blue-grey--text text--darken-1 font-size-12 me-2 caption"
                >
                  {{ selectedLocationOption.title }}
                </p>
                <v-icon size="18" color="blue-grey darken-1"
                  >mdi-chevron-down</v-icon
                >
              </v-btn>
            </template>
          </WidgetMenu>
        </template>
        <div v-else />
        <div
          v-if="
            selectedLocationOption.id !== 'global' &&
            showCompare &&
            !showWidgetError
          "
          class="me-4"
        >
          <CompareButton
            :loading="loadingCompare"
            :disabled="showLoading || loadingCompare"
            :comparing="comparing"
            @click="handleCompareChange"
          />
        </div>
        <slot name="header-action" v-else-if="!showCompare" />
      </div>
    </v-card-title>
    <v-card-text class="ma-0 pa-0 h-100" style="min-height: 157px">
      <div
        v-if="showLoading"
        class="w-100 h-100 d-flex align-center justify-center"
      >
        <div class="loader" />
      </div>
      <div
        v-if="showWidgetError && !showLoading"
        class="blue-grey lighten-3 rounded d-flex align-center justify-center h-100 mx-6"
      >
        <div class="d-flex flex-column">
          <v-icon size="32" color="blue"> mdi-alert-outline </v-icon>
          <p
            class="blue-grey--text text--darken-1 body-2 text-center ma-0 pa-0 mt-3 mb-1"
            style="max-width: 242px"
          >
            {{ $t('insights.widget_error') }}
          </p>
          <v-btn plain rounded color="blue" :ripple="false" @click="refresh">{{
            $t('common.refresh')
          }}</v-btn>
        </div>
      </div>
      <div
        v-if="showWidgetIsEmpty && !showWidgetError && !showLoading"
        class="blue-grey lighten-3 rounded d-flex align-center justify-center h-100 mx-6"
      >
        <div class="d-flex flex-column align-center justify-center">
          <p
            class="blue-grey--text text--darken-1 font-size-14 font-weight-600 text-center ma-0 pa-0 text-center"
            style="max-width: 242px"
          >
            {{ $t('insights.no_data') }}
          </p>
          <p
            class="blue-grey--text text--darken-1 body-2 text-center ma-0 pa-0 text-center"
          >
            {{ $t('insights.widget_empty') }}
          </p>
        </div>
      </div>
      <slot
        name="content"
        v-else-if="!showLoading && !showWidgetError && !showWidgetIsEmpty"
      />
    </v-card-text>
    <v-card-actions class="ma-0 pa-0 py-3 ps-2 pe-3">
      <v-col cols="6" class="ma-0 pa-0">
        <div v-if="!hideTimeMenu && !showWidgetError">
          <WidgetMenu
            closeOnContentClick
            :options="timeOptions"
            @click="setTimeOption"
          >
            <template v-slot:activator="{ on }">
              <v-btn
                v-on="on"
                outlined
                small
                color="blue-grey lighten-3"
                class="ma-0 pa-0 px-2 remove-outline-button"
                data-tourid="insights-widget-filter-date"
                :disabled="showLoading || loadingCompare"
                :ripple="false"
              >
                <p
                  class="ma-0 pa-0 blue-grey--text text--darken-1 font-size-12 me-2 caption"
                >
                  {{ selectedTimeOption.title }}
                </p>
                <v-icon size="18" color="blue-grey darken-1"
                  >mdi-chevron-down</v-icon
                >
              </v-btn>
            </template>
          </WidgetMenu>
        </div>
      </v-col>
      <v-col cols="6" class="ma-0 pa-0 d-flex justify-end">
        <Button
          v-if="cta"
          class="ma-0 px-2"
          color="primary"
          data-testid="widgetBase-button-cta"
          icon="mdi-arrow-right"
          iconPosition="right"
          plain
          size="small"
          truncate
          :disabled="showLoading || loadingCompare"
          :text="cta.text"
          @click="cta.action"
        />

        <slot name="widget-actions" v-if="!cta && !showWidgetError" />
      </v-col>
    </v-card-actions>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { utils } from 'frontend-js-shared'
import { mapGetters } from 'vuex'

import api from '@/api'
import Button from '@/components/UI/Button.vue'
import Menu from '@/components/UI/Menu.vue'
import FeedbackChip from '@/containers/InsightsView/Widgets/SharedComponents/FeedbackChip.vue'
import CompareButton from '@/containers/InsightsView/Widgets/SharedComponents/CompareButton.vue'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

export default {
  name: 'Widget',

  mixins: [mixin, trackerMixin],

  props: {
    translate: Boolean,
    showCompare: Boolean,
    widgetName: String,
    cta: Object,
    customParams: Object,
    hideTimeMenu: Boolean,
    preventDataFetch: Boolean,
    customLoading: Boolean,
    customWidgetIsEmpty: Boolean,
    customWidgetError: Boolean,
  },

  components: {
    Button,
    WidgetMenu: Menu,
    FeedbackChip,
    CompareButton,
  },

  data() {
    return {
      selectedLocationOptionId: '',
      widgetIsEmpty: false,
      widgetError: false,
      loadingCompare: false,
      loading: false,
      comparing: false,
      feedbackGiven: false,
      compareData: null,
      selectedLocationOption: {},
      selectedTimeOption: {
        id: 'l30days',
        title: this.$t('filters.date.last_thirty_days'),
      },
    }
  },

  watch: {
    language() {
      this.selectedTimeOption = this.timeOptions.find(
        option => option.id === this.selectedTimeOption.id
      )

      this.selectedLocationOption = this.locationOptions.find(
        option => option.id === this.selectedLocationOptionId
      ) ?? { id: this.selectedLocationOptionId }

      if (this.translate) this.getInsight()
    },
  },

  mounted() {
    const track = false
    this.setLocationOption(
      { id: this.siteId, title: this.account.site.name },
      track
    )
  },

  computed: {
    ...mapGetters([
      'account',
      'authorizedSiteDetails',
      'organizationSites',
      'siteId',
      'organizationId',
      'language',
    ]),

    timeOptions() {
      return [
        {
          id: 'l7days',
          title: this.$t('filters.date.last_seven_days'),
        },
        {
          id: 'l30days',
          title: this.$t('filters.date.last_thirty_days'),
        },
        {
          id: 'lquarter',
          title: this.$t('filters.date.last_quarter'),
        },
        {
          id: 'ytd',
          title: this.$t('filters.date.year_to_date'),
        },
        {
          id: 'alltime',
          title: this.$t('filters.date.all_time'),
        },
      ]
    },

    showWidgetError() {
      return this.widgetError || this.customWidgetError
    },

    showWidgetIsEmpty() {
      return this.widgetIsEmpty || this.customWidgetIsEmpty
    },

    showLoading() {
      return this.loading || this.customLoading
    },

    name() {
      return this.$t(`insights.widget_names.${_.snakeCase(this.widgetName)}`)
    },

    info() {
      return this.$t(`insights.info.${_.snakeCase(this.widgetName)}`)
    },

    isAdmin() {
      return this.account.isOrganizationAdmin || this.account.isAdmin
    },

    hasMultiSiteScope() {
      if (!this.account?.isSiteAdmin) return false
      if (this.account?.isOrganizationAdmin || this.account?.isAdmin)
        return false
      return this.authorizedSiteDetails.length > 1
    },

    canSelectSite() {
      return this.isAdmin || this.hasMultiSiteScope
    },

    locationOptions() {
      if (this.hasMultiSiteScope) {
        return this.authorizedSiteDetails.map(site => ({
          id: utils.getEntityId(site),
          title: site?.name,
        }))
      }

      return [
        {
          id: 'global',
          title: this.$t('insights.all_sites'),
        },
        ...this.organizationSites.map(site => {
          return {
            id: utils.getEntityId(site),
            title: site?.name,
          }
        }),
      ]
    },

    showFeedbackChip() {
      const feedbackDate = window.localStorage.getItem(
        `widgetFeedbackDate-${this.widgetName}`
      )

      if (!feedbackDate) {
        return true
      }

      const feedbackTime = Number(feedbackDate)
      const currentTime = Date.now()

      const limit_feedback_seconds =
        this.$getFeatureVariable('newInsights', 'limit_feedback_seconds') ?? 30

      const time_blocked = limit_feedback_seconds * 1000

      return currentTime - feedbackTime > time_blocked
    },
  },

  methods: {
    refresh() {
      this.trackInsightsWidgetNavigate({
        widget: this.widgetName,
        action: 'refreshClick',
      })

      this.getInsight()
    },

    checkEmptyObject(object) {
      return _.every(object, value => {
        if (_.isObject(value) && !_.isNull(value)) {
          return this.checkEmptyObject(value)
        }
        return value === 0
      })
    },

    async getInsight() {
      if (this.preventDataFetch) return
      try {
        this.loading = true
        const params = {
          organizationId: this.organizationId,
          insight: this.widgetName,
          ...this.customParams,
        }

        if (!this.hideTimeMenu) {
          params.dateRange = this.selectedTimeOption.id
        }

        if (this.selectedLocationOptionId !== 'global') {
          params.site = this.selectedLocationOptionId
        }

        const response = await api.insights.getInsight(params)

        const widget = response?.data
        this.widgetIsEmpty = this.checkEmptyObject(widget.data)
        this.widgetError = false
        this.$emit('dataFetched', widget)
      } catch (err) {
        this.widgetError = true
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async handleCompareChange(comparing, track = true) {
      if (track) {
        this.trackInsightsWidgetNavigate({
          widget: this.widgetName,
          action: 'compareClick',
          data: { comparing },
        })
      }

      this.comparing = comparing

      if (!this.compareData && comparing) {
        this.compareData = await this.getCompareData()
      }

      return this.$emit('compareClick', this.comparing, this.compareData)
    },

    async getCompareData() {
      try {
        this.loadingCompare = true

        const params = {
          organizationId: this.organizationId,
          insight: this.widgetName,
          dateRange: this.selectedTimeOption.id,
          ...this.customParams,
        }

        const response = await api.insights.getInsight(params)

        return response?.data ?? {}
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingCompare = false
      }
    },

    setLocationOption(option, track = true) {
      this.selectedLocationOption = option
      this.selectedLocationOptionId = option.id

      if (track) {
        this.trackInsightsWidgetNavigate({
          widget: this.widgetName,
          action: 'selectSite',
          siteId: this.selectedLocationOptionId,
        })
      }

      if (option.id === 'global') {
        const compare = false
        const trackCompare = false
        this.handleCompareChange(compare, trackCompare)
      }

      this.$emit('locationOptionChange', this.selectedLocationOptionId)
      this.getInsight()
    },

    async setTimeOption(option) {
      this.$emit('daysOptionChange', option)
      this.compareData = null
      this.selectedTimeOption = option
      await this.$nextTick()
      this.$emit('timeOptionChange', this.selectedTimeOption.id)
      this.getInsight()

      this.trackInsightsWidgetNavigate({
        widget: this.widgetName,
        action: 'selectTimeFrame',
        timeFrame: this.selectedTimeOption.id,
      })

      if (this.comparing) {
        const compare = true
        const trackCompare = false
        this.handleCompareChange(compare, trackCompare)
      }
    },
  },
}
</script>

<style lang="scss">
.insight-card {
  position: relative;
  &:hover {
    .show-hover {
      visibility: visible !important;
    }
  }

  .show-hover {
    visibility: hidden !important;
  }

  .remove-outline-button {
    border: none !important;
    &:hover {
      background-color: $color-blue-grey-lighten3 !important;
    }

    &:focus {
      background-color: $color-blue-grey-lighten3 !important;
    }
  }

  .loader {
    height: 30px;
    aspect-ratio: 2.5;

    --_g: no-repeat
      radial-gradient(farthest-side, #{$color-blue-grey-lighten1} 90%, #0000);

    background: var(--_g), var(--_g), var(--_g), var(--_g);
    background-size: 20% 50%;
    animation: l43 1s infinite linear;
  }

  @keyframes l43 {
    0% {
      background-position:
        calc(0 * 100% / 3) 50%,
        calc(1 * 100% / 3) 50%,
        calc(2 * 100% / 3) 50%,
        calc(3 * 100% / 3) 50%;
    }
    16.67% {
      background-position:
        calc(0 * 100% / 3) 0,
        calc(1 * 100% / 3) 50%,
        calc(2 * 100% / 3) 50%,
        calc(3 * 100% / 3) 50%;
    }
    33.33% {
      background-position:
        calc(0 * 100% / 3) 100%,
        calc(1 * 100% / 3) 0,
        calc(2 * 100% / 3) 50%,
        calc(3 * 100% / 3) 50%;
    }
    50% {
      background-position:
        calc(0 * 100% / 3) 50%,
        calc(1 * 100% / 3) 100%,
        calc(2 * 100% / 3) 0,
        calc(3 * 100% / 3) 50%;
    }
    66.67% {
      background-position:
        calc(0 * 100% / 3) 50%,
        calc(1 * 100% / 3) 50%,
        calc(2 * 100% / 3) 100%,
        calc(3 * 100% / 3) 0;
    }
    83.33% {
      background-position:
        calc(0 * 100% / 3) 50%,
        calc(1 * 100% / 3) 50%,
        calc(2 * 100% / 3) 50%,
        calc(3 * 100% / 3) 100%;
    }
    100% {
      background-position:
        calc(0 * 100% / 3) 50%,
        calc(1 * 100% / 3) 50%,
        calc(2 * 100% / 3) 50%,
        calc(3 * 100% / 3) 50%;
    }
  }
}
</style>
